import React, { useState } from 'react';
import logger from '../../utils/logger';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { Input, Button, Alert, FormField } from '@/components/ui';
import { validateEmail, validateRequired } from '../../utils/validation';
import { usePasswordResetRestriction } from '../../hooks/useDemoMode';

interface ProductionLoginFormProps {
  onLogin: (email: string, password: string) => Promise<void>;
  onResetPassword?: (email: string) => Promise<void>;
  loading?: boolean;
  error?: string;
}

const ProductionLoginForm: React.FC<ProductionLoginFormProps> = ({
  onLogin,
  onResetPassword,
  loading = false,
  error,
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);

  // FL-154: デモモードでのパスワードリセット制限
  const { isPasswordResetRestricted, passwordResetMessage, logPasswordResetAction } =
    usePasswordResetRestriction();

  // フォームバリデーション（統一バリデーション関数を使用）
  React.useEffect(() => {
    const emailValid = validateRequired(email) && validateEmail(email);
    const passwordValid = validateRequired(password) && password.length >= 6;
    setIsFormValid(emailValid && passwordValid);
  }, [email, password]);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    // 入力中はエラーをクリア（ユーザーが修正中なので）
    if (emailTouched && emailError) {
      if (validateEmail(value) || !value) {
        setEmailError('');
      }
    }
  };

  const handleEmailBlur = () => {
    setEmailTouched(true);
    if (email && !validateEmail(email)) {
      setEmailError('有効なメールアドレスを入力してください');
    } else if (!email) {
      setEmailError('メールアドレスは必須です');
    } else {
      setEmailError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isFormValid || loading) {
      return;
    }

    try {
      await onLogin(email, password);
    } catch (error) {
      // エラーハンドリングは親コンポーネントで行う
      logger.error('Login error:', error);
    }
  };

  const handleResetPassword = async () => {
    // FL-154: デモモード制限チェック
    if (isPasswordResetRestricted) {
      logPasswordResetAction('password reset blocked', { email });
      setResetMessage(passwordResetMessage);
      return;
    }

    if (!onResetPassword || !validateEmail(email)) {
      setResetMessage('有効なメールアドレスを入力してください');
      return;
    }

    setIsResettingPassword(true);
    setResetMessage('');

    try {
      await onResetPassword(email);
      setResetMessage('パスワードリセットメールを送信しました。受信箱をご確認ください。');
    } catch (error) {
      logger.error('Password reset error:', error);
      setResetMessage(
        'パスワードリセットに失敗しました。しばらく時間をおいてから再度お試しください。'
      );
    } finally {
      setIsResettingPassword(false);
    }
  };

  return (
    <div className='bg-white dark:bg-gray-800 shadow-lg rounded-xl p-8 border-2 border-gray-200 dark:border-gray-500'>
      <h2 className='text-xl font-bold text-center text-gray-900 dark:text-white mb-6 relative'>
        <span className='bg-gradient-to-r from-gray-900 to-gray-700 dark:from-white dark:to-gray-200 bg-clip-text text-transparent'>
          アカウントにログイン
        </span>
      </h2>

      {error && (
        <Alert variant='error' dismissible={false} className='mb-6'>
          {error}
        </Alert>
      )}

      {resetMessage && (
        <Alert
          variant={resetMessage.includes('送信しました') ? 'success' : 'warning'}
          dismissible={false}
          className='mb-6'
        >
          {resetMessage}
        </Alert>
      )}

      <form onSubmit={handleSubmit} className='space-y-6'>
        <FormField label='メールアドレス' required>
          <Input
            type='email'
            name='email'
            value={email}
            onChange={handleEmailChange}
            onBlur={handleEmailBlur}
            placeholder='example@company.com'
            autoComplete='email'
            disabled={loading}
          />
          {emailTouched && emailError && (
            <p className='mt-1 text-sm text-red-600 dark:text-red-400'>{emailError}</p>
          )}
        </FormField>

        <FormField label='パスワード' required>
          <div className='relative'>
            <Input
              type={showPassword ? 'text' : 'password'}
              name='password'
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder='パスワードを入力'
              autoComplete='current-password'
              disabled={loading}
              className='pr-12'
            />
            <button
              type='button'
              onClick={() => setShowPassword(!showPassword)}
              className='absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-md touch-target'
              disabled={loading}
              aria-label={showPassword ? 'パスワードを隠す' : 'パスワードを表示'}
            >
              {showPassword ? (
                <EyeSlashIcon className='h-5 w-5' />
              ) : (
                <EyeIcon className='h-5 w-5' />
              )}
            </button>
          </div>
        </FormField>

        <Button
          type='submit'
          disabled={!isFormValid || loading}
          loading={loading}
          className='w-full'
          size='lg'
        >
          {loading ? 'ログイン中...' : 'ログイン'}
        </Button>

        <div className='text-center space-y-3'>
          {!isPasswordResetRestricted && onResetPassword && (
            <Button
              type='button'
              variant='ghost'
              onClick={handleResetPassword}
              disabled={loading || isResettingPassword}
              loading={isResettingPassword}
              size='sm'
            >
              {isResettingPassword ? '送信中...' : 'パスワードをお忘れですか？'}
            </Button>
          )}

          {isPasswordResetRestricted && (
            <div className='text-sm text-gray-500 dark:text-gray-400'>{passwordResetMessage}</div>
          )}
        </div>
      </form>
    </div>
  );
};

export default ProductionLoginForm;
